import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { StreamChat } from 'stream-chat';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall } from '../../services/apiUtil';
import { getConfig } from '../../config';
import { resolveImageUrl } from '../../profile/hooks/useProfileImage';

const StreamChatContext = createContext();

// Helper function to get profile image URL
const getProfileImageUrl = (userId, profileImage) => {
  if (!userId || !profileImage) return null;
  if (profileImage.startsWith('/images/')) return resolveImageUrl(profileImage);  // new format
  if (profileImage.startsWith('http')) return null;                                // invalid — not an uploaded image
  return resolveImageUrl(`/images/profile_images/${userId}/${profileImage}`);     // legacy bare filename
};

export const useStreamChat = () => {
  const context = useContext(StreamChatContext);
  if (!context) {
    throw new Error('useStreamChat must be used within a StreamChatProvider');
  }
  return context;
};

export const StreamChatProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [chatClient, setChatClient] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const connectingRef = useRef(false);
  const connectedUserIdRef = useRef(null);
  // Only true once something has actually asked to connect (StreamChatComponent,
  // on mount) — gates the visibility-retry effect below so a user who never
  // opens chat this session doesn't get silently connected just by switching tabs.
  const hasRequestedConnectRef = useRef(false);

  const initChat = useCallback(async () => {
    if (!isAuthenticated || !user) return;
    if (connectingRef.current) return;
    if (connectedUserIdRef.current === String(user.id) && chatClient) return;
    if (document.visibilityState === 'hidden') return;

    try {
      connectingRef.current = true;
      setIsConnecting(true);
      setError(null);

      const apiKey = getConfig('STREAM_API_KEY');
      if (!apiKey) throw new Error('Stream API Key is not configured');

      const client = StreamChat.getInstance(apiKey);

      if (client.userID === String(user.id)) {
        setChatClient(client);
        connectedUserIdRef.current = String(user.id);
        setIsConnecting(false);
        connectingRef.current = false;
        return;
      }

      if (client.userID) {
        await client.disconnectUser();
      }

      const userToken = await getStreamUserToken();

      let profileImageUrl = null;
      if (user.profileImage) {
        profileImageUrl = getProfileImageUrl(user.id, user.profileImage);
      }

      const userName = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || `User ${user.id}`;
      const avatarUrl = profileImageUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=random&size=200`;

      await client.connectUser(
        { id: String(user.id), name: userName, image: avatarUrl },
        userToken
      );

      setChatClient(client);
      connectedUserIdRef.current = String(user.id);
      setIsConnecting(false);
      connectingRef.current = false;

    } catch (err) {
      console.error('Error initializing Stream Chat:', err);
      setError(err.message);
      setIsConnecting(false);
      connectingRef.current = false;
    }
  }, [isAuthenticated, user?.id]);

  // Connect Stream Chat — called explicitly by StreamChatComponent on mount
  // (both the Messages page and squad Chat tabs render it), not automatically
  // on login. Keeps every other page free of a live Stream WebSocket
  // connection for users who never actually open chat this session.
  const connectChat = useCallback(() => {
    hasRequestedConnectRef.current = true;
    initChat();
  }, [initChat]);

  // Disconnect on logout. If user logs out, null the client first so React
  // unmounts channel components before disconnectUser() is called. Calling
  // disconnect() while Channel components are still mounted triggers their
  // cleanup which calls channel.getClient() on an already-disconnected
  // client → 415.
  useEffect(() => {
    if (isAuthenticated || !chatClient) return;
    const clientToDisconnect = chatClient;
    setChatClient(null);
    connectedUserIdRef.current = null;
    setTimeout(() => clientToDisconnect.disconnectUser().catch(() => {}), 0);
  }, [isAuthenticated, chatClient]);

  // Retry Stream Chat init when returning from background if it failed to
  // connect — only once the user has actually opened chat this session,
  // otherwise this would silently connect chat for everyone on tab-focus.
  useEffect(() => {
    if (!isAuthenticated || !hasRequestedConnectRef.current) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !chatClient && !connectingRef.current) {
        initChat();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isAuthenticated, chatClient, initChat]);

  // Function to get Stream user token from backend
  const getStreamUserToken = async () => {
    try {
      const data = await apiCall('/stream/generate-token', {
        method: 'POST',
        body: JSON.stringify({ userId: String(user.id) }),
      });
      return data.token;
    } catch (error) {
      console.error('Error getting Stream token:', error);
      throw new Error('Failed to authenticate with chat service. Please contact support.');
    }
  };

  // Create or get a marketplace channel between current user (buyer) and a seller for a listing
  const createOrGetMarketplaceChannel = useCallback(async (sellerId, listingData) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    const result = await apiCall('/stream/create-marketplace-channel', {
      method: 'POST',
      body: JSON.stringify({
        buyerId: user.id,
        sellerId,
        listingId: listingData.id,
        listingTitle: listingData.title,
        listingPrice: listingData.price,
        listingImage: resolveImageUrl(listingData.image) || '',
      }),
    });

    return { channelId: result.channelId, created: result.created };
  }, [user]);

  // Function to update the current user's profile image in Stream Chat
  const updateUserProfileImage = useCallback(async (newProfileImage) => {
    if (!chatClient || !user) return;

    try {
      let profileImageUrl = null;
      if (newProfileImage) {
        profileImageUrl = getProfileImageUrl(user.id, newProfileImage);
      }

      const userName = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || `User ${user.id}`;
      const avatarUrl = profileImageUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=random&size=200`;

      // Update user in Stream Chat
      await chatClient.partialUpdateUser({
        id: String(user.id),
        set: {
          image: avatarUrl,
        },
      });

    } catch (error) {
      console.error('Error updating profile image in Stream Chat:', error);
    }
  }, [chatClient, user]);

  const value = {
    chatClient,
    isConnecting,
    error,
    user,
    connectChat,
    createOrGetMarketplaceChannel,
    updateUserProfileImage,
  };

  return (
    <StreamChatContext.Provider value={value}>
      {children}
    </StreamChatContext.Provider>
  );
};
